import {
  FoodItemTag,
  FoodItemTagCreateInBatch,
  FoodTag,
  FoodTagCategory,
} from "client/jspPlatformExperiment";

export const filterIngredientLevelFoodTags = (
  foodTags: (FoodItemTag | FoodTag)[],
): (FoodItemTag | FoodTag)[] =>
  foodTags.filter(
    (tag) =>
      !(
        tag.tag_categories.includes(FoodTagCategory.DIET_TYPE) ||
        tag.tag_categories.includes(FoodTagCategory.FOOD_TEXTURE) ||
        tag.tag_categories.includes(FoodTagCategory.LIQUID_THICKNESS) ||
        (tag.tag_categories.includes(FoodTagCategory.PREFERENCE) &&
          !tag.tag_categories.includes(FoodTagCategory.ALLERGEN))
      ),
  );

export const filterRecipeLevelFoodTags = (
  foodTags: (FoodItemTag | FoodTag)[],
): (FoodItemTag | FoodTag)[] =>
  foodTags.filter(
    (tag) =>
      tag.tag_categories.includes(FoodTagCategory.DIET_TYPE) ||
      tag.tag_categories.includes(FoodTagCategory.FOOD_TEXTURE) ||
      tag.tag_categories.includes(FoodTagCategory.LIQUID_THICKNESS) ||
      (tag.tag_categories.includes(FoodTagCategory.PREFERENCE) &&
        !tag.tag_categories.includes(FoodTagCategory.ALLERGEN)),
  );

export const filterOutIsFoodTag = (
  foodTags: (FoodItemTag | FoodTag)[],
): (FoodItemTag | FoodTag)[] =>
  foodTags.filter(
    (tag) => !tag.tag_categories.includes(FoodTagCategory.IS_FOOD),
  );

export const isVariantTypeTag = (tag: FoodItemTag | FoodTag): boolean =>
  tag.tag_categories.includes(FoodTagCategory.VARIANT_TYPE);

export const filterVariantTypeTags = <T extends FoodItemTag | FoodTag>(
  foodTags: T[],
): T[] => foodTags.filter(isVariantTypeTag);

/**
 * A food item tag flagged as defining its variant's identity (chosen as the
 * variant type when the variant was created / its type edited). Distinct from
 * `isVariantTypeTag`: that marks a tag as *eligible* to be a variant type
 * (VARIANT_TYPE category on the tag itself), while this flag is per food item
 * — the same tag can sit on another variant as a plain diet/texture tag.
 */
export const isVariantIdentityTag = (tag: FoodItemTag): boolean =>
  !!tag.is_variant_identity;

export const filterVariantIdentityTags = <T extends FoodItemTag>(
  foodTags: T[],
): T[] => foodTags.filter(isVariantIdentityTag);

export type VariantTypeLabelLanguage = "en" | "fr";

/**
 * Display-only renames applied when a tag is shown AS A VARIANT TYPE (variant
 * tabs, derived variant names, the variant-type picker, menu/order variant
 * labels). Keyed by tag ID — IDs are stable while tag names can be edited.
 * The tag itself (id, categories, labels elsewhere — e.g. the Suitable Diet
 * Type section) is untouched.
 */
const VARIANT_TYPE_DISPLAY_OVERRIDES: Record<
  number,
  Record<VariantTypeLabelLanguage, string>
> = {
  // 62 = Lactose Intolerance
  62: { en: "Lactose free", fr: "Sans Lactose" },
};

/**
 * Label for a tag in variant-type contexts: the display override when one
 * exists, otherwise the tag's own label for that language (`tag_description`
 * is English, `tag_name` is French — may be null/undefined, callers keep
 * their own fallbacks).
 */
export const variantTypeTagLabel = (
  tag: FoodItemTag | FoodTag,
  language: VariantTypeLabelLanguage,
): string | null | undefined => {
  const override = VARIANT_TYPE_DISPLAY_OVERRIDES[tag.id];
  if (override) {
    return override[language];
  }
  return language === "en" ? tag.tag_description : tag.tag_name;
};

/**
 * Tags to persist on a variant food item: the variant-identity tags that
 * define the variant plus recipe-level tags, de-duplicated by id. Identity
 * tags come first so a tag that is both keeps its is_variant_identity flag.
 */
export const filterVariantSaveTags = <T extends FoodItemTag>(
  foodTags: T[],
): T[] => {
  const merged = [
    ...filterVariantIdentityTags(foodTags),
    ...filterRecipeLevelFoodTags(foodTags),
  ] as T[];
  const seenIds = new Set<number>();
  return merged.filter((tag) => {
    if (seenIds.has(tag.id)) {
      return false;
    }
    seenIds.add(tag.id);
    return true;
  });
};

export const toFoodItemTags = (
  tags: FoodItemTag[],
): FoodItemTagCreateInBatch[] => tags.map((t) => ({ tag_id: t.id }));

/**
 * Create-path variant: strip the is_food tag before mapping,
 * since the server derives it on newly created ingredients.
 */
export const toFoodItemTagsForCreate = (
  tags: FoodItemTag[],
): FoodItemTagCreateInBatch[] =>
  filterOutIsFoodTag(tags).map((t) => ({ tag_id: t.id }));
